import React, { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { fmtCurrency, fmtPerPulse, calculate } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Clock, Tag, Pencil, X, Zap, FileText, ChevronUp, ChevronDown, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { ClientImport } from "@/components/booking/ClientImport";
import { CATEGORY_COLORS } from "@/lib/palette";

// Earthy palette that complements the app theme (sage primary, terracotta secondary, champagne accent)
const COLORS = CATEGORY_COLORS;
const FREQ_PER_MONTH = { weekly: 52 / 12, monthly: 1, quarterly: 1 / 3, annual: 1 / 12 };
const emptyService = { name: "", category_id: "", subcategory_id: "", duration_min: 30, buffer_min: 0, price: 0, color: "#6B8E7B", staff_ids: [], cost: { materials: [], overhead: 0, use_staff_time: true, shots: 0 }, active: true };

export default function Services() {
  const { business, user } = useAuth();
  const isOwner = user?.role === "owner";
  const cur = business?.currency || "AUD";
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(emptyService);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [catName, setCatName] = useState("");
  const [devices, setDevices] = useState([]);
  const [formTpls, setFormTpls] = useState([]);
  const [afterTpls, setAfterTpls] = useState([]);
  const [biz, setBiz] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCost = (k, v) => setForm((f) => ({ ...f, cost: { ...f.cost, [k]: v } }));

  const load = () => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/subcategories").then(({ data }) => setSubcats(data)).catch(() => {});
    api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {});
    api.get("/devices").then(({ data }) => setDevices(data)).catch(() => {});
    api.get("/forms/templates").then(({ data }) => setFormTpls(data)).catch(() => {});
    api.get("/aftercare/templates").then(({ data }) => setAfterTpls(data)).catch(() => {});
    api.get("/business").then(({ data }) => setBiz(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const hourly = useMemo(() => {
    const rates = staff.map((s) => s.base_hourly_rate || 0).filter(Boolean);
    return rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 32;
  }, [staff]);

  // Business overhead spread across each operating hour (from the Fixed Operating Costs page)
  const overheadPerHour = useMemo(() => {
    const rows = biz?.fixed_costs || [];
    const monthly = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0) * (FREQ_PER_MONTH[r.frequency] || 1), 0);
    const oph = parseFloat(biz?.operating_hours_per_month) || 0;
    return oph > 0 ? monthly / oph : 0;
  }, [biz]);

  const devicePerPulse = (dev) => (dev?.params ? (calculate(dev.params).fullyLoadedPerPulse || 0) : 0);
  const catDevice = (catId) => {
    const c = categories.find((x) => x.id === catId);
    return c?.device_id ? devices.find((d) => d.id === c.device_id) : null;
  };

  const breakdown = (svc) => {
    const mat = (svc.cost?.materials || []).reduce((s, m) => s + (parseFloat(m.cost) || 0), 0);
    const hours = (svc.duration_min || 0) / 60;
    const overhead = overheadPerHour * hours + (parseFloat(svc.cost?.overhead) || 0);
    const staffTime = svc.cost?.use_staff_time ? hours * hourly : 0;
    const dev = catDevice(svc.category_id);
    const shots = parseFloat(svc.cost?.shots) || 0;
    const laser = dev && shots ? devicePerPulse(dev) * shots : 0;
    return { mat, overhead, staffTime, laser, total: mat + overhead + staffTime + laser };
  };
  const costOf = (svc) => breakdown(svc).total;

  const openNew = () => { setForm(emptyService); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...emptyService, ...s, cost: { ...emptyService.cost, ...s.cost } }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Service name required"); return; }
    try {
      if (editId) await api.put(`/services/${editId}`, form);
      else await api.post("/services", form);
      setOpen(false); load(); toast.success("Service saved");
    } catch { toast.error("Couldn't save service"); }
  };
  const remove = async (s) => { try { await api.delete(`/services/${s.id}`); load(); toast.success("Deleted"); } catch { toast.error("Failed"); } };

  const addCategory = async () => {
    if (!catName.trim()) return;
    try { await api.post("/categories", { name: catName.trim(), color: COLORS[categories.length % COLORS.length] }); setCatName(""); load(); toast.success("Category added"); }
    catch { toast.error("Failed"); }
  };

  const removeCategory = async (c) => {
    const n = services.filter((s) => s.category_id === c.id).length;
    const msg = n ? `Delete "${c.name}"? Its ${n} service${n > 1 ? "s" : ""} will move to Uncategorised.` : `Delete "${c.name}"?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/categories/${c.id}`); load(); toast.success("Category deleted"); }
    catch { toast.error("Failed"); }
  };

  const setCategoryDevice = async (c, device_id) => {
    try { await api.put(`/categories/${c.id}`, { name: c.name, color: c.color, device_id: device_id || "" }); load(); toast.success(device_id ? "Laser attached" : "Laser removed"); }
    catch { toast.error("Failed"); }
  };

  const setCategoryColor = async (c, color) => {
    try { await api.put(`/categories/${c.id}`, { name: c.name, color, device_id: c.device_id || "" }); load(); toast.success("Colour updated"); }
    catch { toast.error("Failed"); }
  };

  const setCategoryForms = async (c, patch) => {
    try { await api.put(`/categories/${c.id}`, { name: c.name, ...patch }); load(); toast.success("Category forms updated"); }
    catch { toast.error("Failed"); }
  };

  const [newSub, setNewSub] = useState({});
  const addSub = async (catId) => {
    const name = (newSub[catId] || "").trim();
    if (!name) return;
    try { await api.post("/subcategories", { category_id: catId, name }); setNewSub((s) => ({ ...s, [catId]: "" })); load(); toast.success("Sub-category added"); }
    catch { toast.error("Failed"); }
  };
  const renameSub = async (sc) => {
    const name = window.prompt("Rename sub-category", sc.name);
    if (!name || !name.trim()) return;
    try { await api.put(`/subcategories/${sc.id}`, { category_id: sc.category_id, name: name.trim() }); load(); toast.success("Renamed"); }
    catch { toast.error("Failed"); }
  };
  const removeSub = async (sc) => {
    const n = services.filter((s) => s.subcategory_id === sc.id).length;
    if (!window.confirm(n ? `Delete "${sc.name}"? Its ${n} service${n > 1 ? "s" : ""} will stay in the category but lose the sub-category.` : `Delete "${sc.name}"?`)) return;
    try { await api.delete(`/subcategories/${sc.id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };
  const subsOf = (catId) => subcats.filter((s) => s.category_id === catId);


  const toggleStaff = (id) => set("staff_ids", form.staff_ids.includes(id) ? form.staff_ids.filter((x) => x !== id) : [...form.staff_ids, id]);
  const addMaterial = () => setCost("materials", [...(form.cost.materials || []), { name: "", cost: 0 }]);
  const updMaterial = (i, k, v) => setCost("materials", form.cost.materials.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const rmMaterial = (i) => setCost("materials", form.cost.materials.filter((_, idx) => idx !== i));

  const catName_ = (id) => categories.find((c) => c.id === id)?.name || "Uncategorised";
  const subName = (id) => subcats.find((x) => x.id === id)?.name;
  // Profit per hour = profit ÷ appointment minutes × 60 (how much profit the treatment earns for the time it occupies — NOT margin %).
  const profitPerHour = (svc) => {
    const mins = Number(svc.duration_min) || 0;
    if (mins <= 0) return null;
    return (((Number(svc.price) || 0) - breakdown(svc).total) / mins) * 60;
  };
  const [sortBy, setSortBy] = useState("order");
  const sortItems = (items) => sortBy === "profit_hr" ? [...items].sort((a, b) => (profitPerHour(b) ?? -Infinity) - (profitPerHour(a) ?? -Infinity)) : items;
  const byCat = categories.map((c) => ({ cat: c, items: sortItems(services.filter((s) => s.category_id === c.id)) }));
  const uncat = sortItems(services.filter((s) => !categories.find((c) => c.id === s.category_id)));
  const [dragId, setDragId] = useState(null);
  const [dragCat, setDragCat] = useState(null);
  const dropOnCat = (targetId) => {
    if (!dragCat || dragCat === targetId) { setDragCat(null); return; }
    const ids = categories.map((c) => c.id);
    const from = ids.indexOf(dragCat), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragCat(null); return; }
    const reordered = [...categories];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setCategories(reordered);
    api.post("/categories/reorder", { ids: reordered.map((c) => c.id) }).catch(() => load());
    setDragCat(null);
  };
  const persistOrder = (items, reordered) => {
    const groupIds = new Set(items.map((s) => s.id));
    let k = 0;
    const newOrder = services.map((s) => (groupIds.has(s.id) ? reordered[k++] : s));
    setServices(newOrder);
    api.post("/services/reorder", { ids: newOrder.map((s) => s.id) }).catch(() => load());
  };
  const dropOnCard = (items, targetId) => {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const ids = items.map((s) => s.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const reordered = [...items];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    persistOrder(items, reordered);
    setDragId(null);
  };
  const moveService = async (items, idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    const groupIds = new Set(items.map((s) => s.id));
    let k = 0;
    const newOrder = services.map((s) => (groupIds.has(s.id) ? reordered[k++] : s));
    setServices(newOrder);
    try { await api.post("/services/reorder", { ids: newOrder.map((s) => s.id) }); } catch { load(); }
  };

  const liveCost = costOf(form);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Services</h1>
          <p className="text-muted-foreground mt-1">Categories, pricing & cost of doing each service</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-border p-0.5 text-xs" data-testid="services-sort">
            {[["order", "Menu order"], ["profit_hr", "Profit / hr"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setSortBy(k)} className={`px-3 h-8 rounded-md transition-colors ${sortBy === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid={`services-sort-${k}`}>{l}</button>
            ))}
          </div>
          {isOwner && (
            <>
              <ClientImport resource="services" onDone={load} />
              <Button className="rounded-lg" onClick={openNew} data-testid="add-service-button"><Plus className="h-4 w-4 mr-1.5" /> New service</Button>
            </>
          )}
        </div>
      </div>
      {sortBy === "profit_hr" && <p className="text-xs text-muted-foreground -mt-3 mb-4" data-testid="services-sort-hint">One list across all categories, highest → lowest profit per hour. Profit ÷ duration × 60 — how much each treatment earns for the appointment time it takes (not margin %).</p>}

      {/* categories quick add */}
      <Card className="rounded-2xl border bg-card p-4 shadow-sm mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <Tag className="h-4 w-4 text-muted-foreground" />
          {categories.map((c) => (
            <Badge key={c.id} draggable={isOwner} onDragStart={isOwner ? () => setDragCat(c.id) : undefined} onDragEnd={isOwner ? () => setDragCat(null) : undefined} onDragOver={isOwner ? (e) => e.preventDefault() : undefined} onDrop={isOwner ? () => dropOnCat(c.id) : undefined}
              style={{ backgroundColor: `${c.color}22`, color: c.color }} className={`border-0 flex items-center gap-1 pr-1.5 ${isOwner ? "cursor-grab active:cursor-grabbing" : ""} ${dragCat === c.id ? "opacity-40" : ""}`} data-testid={`category-badge-${c.id}`}>
              {isOwner && <GripVertical className="h-3 w-3 opacity-50 -ml-0.5" />}
              {c.name}
              {isOwner && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="ml-0.5 h-3.5 w-3.5 rounded-full border border-black/25" style={{ backgroundColor: c.color }} title={`Change ${c.name} colour`} data-testid={`category-color-${c.id}`} />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" align="start">
                    <div className="flex gap-1.5">
                      {COLORS.map((col) => <button key={col} onClick={() => setCategoryColor(c, col)} className={`h-6 w-6 rounded-full ${c.color === col ? "ring-2 ring-offset-1 ring-foreground" : ""}`} style={{ backgroundColor: col }} data-testid={`category-color-${c.id}-opt-${col}`} />)}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {isOwner && (
                <button onClick={() => removeCategory(c)} className="ml-0.5 rounded-full hover:bg-black/10 p-0.5" title={`Delete ${c.name}`} data-testid={`delete-category-${c.id}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
          <div className="flex items-center gap-2 ml-auto">
            {isOwner && <>
              <Input placeholder="Add category" value={catName} onChange={(e) => setCatName(e.target.value)} className="h-9 rounded-lg w-44" data-testid="category-name" />
              <Button size="sm" variant="outline" className="rounded-lg" onClick={addCategory} data-testid="add-category-button">Add</Button>
            </>}
          </div>
        </div>
      </Card>

      {/* attach a laser device to a category → its running cost flows into every service in it */}
      {isOwner && devices.length > 0 && categories.length > 0 && (
        <Card className="rounded-2xl border bg-card p-4 shadow-sm mb-6" data-testid="laser-attach-card">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="h-4 w-4 text-primary" />
            <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Attach a laser to a category</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Pick a saved laser for a category and its running cost is added to every service in that category (scaled by each service's pulses).</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {categories.map((c) => (
              <div key={c.id} className="flex items-center gap-2" data-testid={`laser-attach-row-${c.id}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                <span className="text-sm flex-1 truncate">{c.name}</span>
                <Select value={c.device_id || "none"} onValueChange={(v) => setCategoryDevice(c, v === "none" ? "" : v)}>
                  <SelectTrigger className="h-9 rounded-lg w-48" data-testid={`laser-select-${c.id}`}><SelectValue placeholder="No laser" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No laser</SelectItem>
                    {devices.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} · {fmtPerPulse(devicePerPulse(d), cur)}/pulse</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isOwner && categories.length > 0 && (
        <Card className="rounded-2xl border bg-card p-4 shadow-sm mb-6" data-testid="category-forms-card">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-4 w-4 text-primary" />
            <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Consult form & aftercare per category</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Pick a consult form and an aftercare/prep template for each category. When a client books a service in that category, these replace the clinic-default templates in whichever automated messages have "Attach a form" / "Attach instructions" switched on in Messages. Turn attachments on or off per message in Messages — leave on "Clinic default" here to use the templates set there.</p>
          <div className="space-y-2">
            {categories.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center gap-2" data-testid={`cat-forms-row-${c.id}`}>
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                <span className="text-sm flex-1 min-w-[120px] truncate">{c.name}</span>
                <Select value={c.consult_form_id || "none"} onValueChange={(v) => setCategoryForms(c, { consult_form_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 rounded-lg w-52" data-testid={`cat-consult-${c.id}`}><SelectValue placeholder="Consult: clinic default" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Consult: clinic default</SelectItem>
                    {formTpls.map((t) => <SelectItem key={t.id} value={t.id}>Consult: {t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={c.aftercare_template_id || "none"} onValueChange={(v) => setCategoryForms(c, { aftercare_template_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 rounded-lg w-52" data-testid={`cat-aftercare-${c.id}`}><SelectValue placeholder="Aftercare: clinic default" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aftercare: clinic default</SelectItem>
                    {afterTpls.map((t) => <SelectItem key={t.id} value={t.id}>Aftercare: {t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isOwner && categories.length > 0 && (
        <Card className="rounded-2xl border bg-card p-4 shadow-sm mb-6" data-testid="subcategory-card">
          <div className="flex items-center gap-2 mb-3">
            <Tag className="h-4 w-4 text-primary" />
            <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Sub-categories</span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Create sub-categories under a category (e.g. Laser → Face, Body, Underarms). Assign a service to a sub-category from its editor. They group your online booking page so clients aren't overwhelmed.</p>
          <div className="space-y-3">
            {categories.map((c) => (
              <div key={c.id} className="rounded-xl border border-border/70 p-3" data-testid={`subcat-group-${c.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                  <span className="text-sm font-medium flex-1 truncate">{c.name}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {subsOf(c.id).map((sc) => (
                    <Badge key={sc.id} style={{ backgroundColor: `${c.color}22`, color: c.color }} className="border-0 flex items-center gap-1 pr-1" data-testid={`subcat-badge-${sc.id}`}>
                      <button onClick={() => renameSub(sc)} className="hover:underline" data-testid={`subcat-rename-${sc.id}`}>{sc.name}</button>
                      <button onClick={() => removeSub(sc)} className="rounded-full hover:bg-black/10 p-0.5" data-testid={`subcat-delete-${sc.id}`}><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                  <div className="flex items-center gap-1">
                    <Input value={newSub[c.id] || ""} onChange={(e) => setNewSub((s) => ({ ...s, [c.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addSub(c.id)} placeholder="Add sub-category" className="h-8 rounded-lg w-40 text-sm" data-testid={`subcat-input-${c.id}`} />
                    <Button size="sm" variant="outline" className="rounded-lg h-8" onClick={() => addSub(c.id)} data-testid={`subcat-add-${c.id}`}>Add</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="space-y-6">
        {(sortBy === "profit_hr"
          ? [{ cat: { id: "_all", name: "All services — highest to lowest profit per hour", color: "#3E4552" }, items: sortItems(services) }]
          : [...byCat, { cat: { id: "_un", name: "Uncategorised", color: "#94A3B8" }, items: uncat }]).filter((g) => g.items.length).map((g) => (
          <div key={g.cat.id}>
            <div className="flex items-center gap-2 mb-2"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: g.cat.color }} /><h3 className="font-heading font-semibold">{g.cat.name}</h3></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.items.map((s, si) => {
                const bd = breakdown(s); const cost = bd.total; const profit = (s.price || 0) - cost; const pph = profitPerHour(s);
                return (
                  <Card key={s.id} className={`rounded-2xl border bg-card p-4 shadow-sm transition-opacity ${dragId === s.id ? "opacity-40" : ""}`} data-testid={`service-card-${s.id}`}
                    onDragOver={isOwner ? (e) => e.preventDefault() : undefined}
                    onDrop={isOwner ? () => dropOnCard(g.items, s.id) : undefined}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        {isOwner && <span draggable onDragStart={() => setDragId(s.id)} onDragEnd={() => setDragId(null)} className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-muted-foreground shrink-0 -ml-1" data-testid={`service-drag-${s.id}`}><GripVertical className="h-4 w-4" /></span>}
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: g.cat.id === "_un" ? s.color : g.cat.color }} />
                        <span className="font-medium truncate">{s.name}</span>
                      </div>
                      <div className="flex gap-1 shrink-0 items-center">
                        {isOwner && <>
                          <button onClick={() => moveService(g.items, si, -1)} disabled={si === 0} className="text-muted-foreground hover:text-primary disabled:opacity-25" data-testid={`service-up-${s.id}`}><ChevronUp className="h-4 w-4" /></button>
                          <button onClick={() => moveService(g.items, si, 1)} disabled={si === g.items.length - 1} className="text-muted-foreground hover:text-primary disabled:opacity-25" data-testid={`service-down-${s.id}`}><ChevronDown className="h-4 w-4" /></button>
                          <button onClick={() => openEdit(s)} className="text-muted-foreground hover:text-primary" data-testid={`service-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => remove(s)} className="text-muted-foreground hover:text-destructive" data-testid={`service-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                        </>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration_min}m{s.buffer_min ? ` +${s.buffer_min}` : ""}</span>
                      {s.subcategory_id && subName(s.subcategory_id) && <span className="flex items-center gap-1" data-testid={`service-subcat-${s.id}`}><Tag className="h-3 w-3" />{subName(s.subcategory_id)}</span>}
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-3 text-center">
                      <div><div className="text-[10px] text-muted-foreground uppercase">Price</div><div className="font-heading font-bold tabular-nums">{fmtCurrency(s.price, cur, 0)}</div></div>
                      <div><div className="text-[10px] text-muted-foreground uppercase">Cost</div><div className="font-medium tabular-nums text-muted-foreground">{fmtCurrency(cost, cur, 0)}</div></div>
                      <div><div className="text-[10px] text-muted-foreground uppercase">Profit</div><div className="font-heading font-bold tabular-nums text-primary">{fmtCurrency(profit, cur, 0)}</div><div className="text-[10px] text-muted-foreground tabular-nums" data-testid={`service-margin-${s.id}`}>{s.price > 0 ? Math.round((profit / s.price) * 100) : 0}% margin</div></div>
                      <div><div className="text-[10px] text-muted-foreground uppercase">Profit / hr</div><div className={`font-heading font-bold tabular-nums ${pph !== null && pph < 0 ? "text-destructive" : "text-primary"}`} data-testid={`service-profit-hr-${s.id}`}>{pph === null ? "—" : fmtCurrency(pph, cur, 0)}</div><div className="text-[10px] text-muted-foreground tabular-nums">{s.duration_min ? `${s.duration_min} min` : "no duration"}</div></div>
                    </div>
                    {bd.laser > 0 && (
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-2 pt-2 border-t" data-testid={`service-laser-line-${s.id}`}>
                        <Zap className="h-3 w-3 text-primary" />incl. laser {fmtCurrency(bd.laser, cur)} · {parseFloat(s.cost?.shots) || 0} pulses
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        ))}
        {services.length === 0 && <Card className="rounded-2xl border bg-card p-10 text-center text-muted-foreground">No services yet — add your first.</Card>}
      </div>

      {/* Service dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <Input placeholder="Service name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-lg" data-testid="service-name" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v, subcategory_id: "" }))}>
                <SelectTrigger className="h-11 rounded-lg" data-testid="service-category"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                {form.category_id ? (
                  <span className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="service-inherits-color">
                    <span className="h-4 w-4 rounded-full shrink-0" style={{ backgroundColor: categories.find((c) => c.id === form.category_id)?.color || form.color }} />
                    Uses category colour
                  </span>
                ) : (
                  COLORS.map((c) => <button key={c} onClick={() => set("color", c)} className={`h-6 w-6 rounded-full ${form.color === c ? "ring-2 ring-offset-2 ring-offset-background ring-foreground" : ""}`} style={{ backgroundColor: c }} data-testid={`service-color-${c}`} />)
                )}
              </div>
            </div>
            {form.category_id && subsOf(form.category_id).length > 0 && (
              <Select value={form.subcategory_id || "none"} onValueChange={(v) => set("subcategory_id", v === "none" ? "" : v)}>
                <SelectTrigger className="h-11 rounded-lg" data-testid="service-subcategory"><SelectValue placeholder="Sub-category (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No sub-category</SelectItem>
                  {subsOf(form.category_id).map((sc) => <SelectItem key={sc.id} value={sc.id}>{sc.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-xs text-muted-foreground">Duration (min)</label><Input type="number" min="0" step="5" value={form.duration_min} onChange={(e) => set("duration_min", parseInt(e.target.value) || 0)} className="h-11 rounded-lg" data-testid="service-duration" /></div>
              <div><label className="text-xs text-muted-foreground">Buffer (min)</label><Input type="number" min="0" step="5" value={form.buffer_min} onChange={(e) => set("buffer_min", parseInt(e.target.value) || 0)} className="h-11 rounded-lg" data-testid="service-buffer" /></div>
              <div><label className="text-xs text-muted-foreground">Price</label><Input type="number" min="0" value={form.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} className="h-11 rounded-lg" data-testid="service-price" /></div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Rebook reminder after (weeks)</label>
              <Input type="number" min="1" max="52" value={form.rebook_weeks ?? ""} placeholder="Clinic default" onChange={(e) => set("rebook_weeks", e.target.value === "" ? null : Math.max(1, parseInt(e.target.value) || 1))} className="h-11 rounded-lg" data-testid="service-rebook-weeks" />
              <p className="text-[11px] text-muted-foreground mt-1">When this was the client's last treatment, the automatic rebook text goes out this many weeks after the visit (e.g. brows 3, laser 6). Blank = the clinic-wide setting in CRM.</p>
            </div>

            <label className="flex items-start gap-2 text-sm rounded-lg border p-3 cursor-pointer" data-testid="service-one-visit">
              <input type="checkbox" className="mt-0.5" checked={!!form.one_visit} onChange={(e) => set("one_visit", e.target.checked)} />
              <span><span className="font-medium">One-visit service</span><span className="block text-[11px] text-muted-foreground">Consults, patch tests and one-off treatments: clients who only have this never count against your rebooking rate and get no rebook text.</span></span>
            </label>

            <div>
              <label className="text-xs text-muted-foreground">Staff who can perform</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {staff.map((s) => (
                  <button key={s.id} onClick={() => toggleStaff(s.id)} data-testid={`service-staff-${s.id}`}
                    className={`px-3 py-1 rounded-full text-xs border ${form.staff_ids.includes(s.id) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>{s.name}</button>
                ))}
              </div>
            </div>

            {/* cost of service */}
            <div className="rounded-xl border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Cost of doing the service</span>
                <span className="font-heading font-bold text-primary tabular-nums" data-testid="service-live-cost">{fmtCurrency(liveCost, cur)}</span>
              </div>
              {(form.cost.materials || []).map((m, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Material" value={m.name} onChange={(e) => updMaterial(i, "name", e.target.value)} className="h-9 rounded-lg flex-1" />
                  <Input type="number" min="0" value={m.cost} onChange={(e) => updMaterial(i, "cost", e.target.value)} className="h-9 rounded-lg w-24" />
                  <button onClick={() => rmMaterial(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <button onClick={addMaterial} className="text-sm text-primary flex items-center gap-1" data-testid="service-add-material"><Plus className="h-3.5 w-3.5" /> Material</button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Extra overhead</span>
                  <Input type="number" min="0" value={form.cost.overhead} onChange={(e) => setCost("overhead", parseFloat(e.target.value) || 0)} className="h-9 rounded-lg w-24" data-testid="service-overhead" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-muted-foreground">Include staff time ({form.duration_min}m @ {fmtCurrency(hourly, cur, 0)}/hr)</span>
                <Switch checked={form.cost.use_staff_time} onCheckedChange={(v) => setCost("use_staff_time", v)} data-testid="service-use-time" />
              </div>

              {catDevice(form.category_id) && (
                <div className="rounded-lg bg-primary/5 border border-primary/20 p-2.5 space-y-2" data-testid="service-laser-block">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium flex items-center gap-1"><Zap className="h-3.5 w-3.5 text-primary" />{catDevice(form.category_id).name}</span>
                    <span className="text-[11px] text-muted-foreground">{fmtPerPulse(devicePerPulse(catDevice(form.category_id)), cur)}/pulse</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">Pulses per treatment</span>
                    <Input type="number" min="0" value={form.cost.shots || 0} onChange={(e) => setCost("shots", parseFloat(e.target.value) || 0)} className="h-9 rounded-lg w-28 tabular-nums" placeholder="e.g. 200" data-testid="service-shots" />
                  </div>
                </div>
              )}

              {/* live breakdown */}
              <div className="pt-2 mt-1 border-t space-y-1 text-[11px] text-muted-foreground">
                {(() => { const bd = breakdown(form); return (<>
                  <div className="flex justify-between"><span>Materials</span><span className="tabular-nums">{fmtCurrency(bd.mat, cur)}</span></div>
                  <div className="flex justify-between"><span>Overhead (from Fixed Operating Costs)</span><span className="tabular-nums">{fmtCurrency(bd.overhead, cur)}</span></div>
                  <div className="flex justify-between"><span>Staff time</span><span className="tabular-nums">{fmtCurrency(bd.staffTime, cur)}</span></div>
                  {bd.laser > 0 && <div className="flex justify-between text-primary"><span>Laser running cost</span><span className="tabular-nums">{fmtCurrency(bd.laser, cur)}</span></div>}
                  <div className="flex justify-between font-medium text-foreground pt-1 border-t"><span>Profit margin</span><span className="tabular-nums">{form.price > 0 ? Math.round(((form.price - bd.total) / form.price) * 100) : 0}% ({fmtCurrency((form.price || 0) - bd.total, cur)})</span></div>
                  <div className="flex justify-between font-medium text-primary"><span>Profit / hr</span><span className="tabular-nums" data-testid="form-profit-hr">{Number(form.duration_min) > 0 ? fmtCurrency((((form.price || 0) - bd.total) / Number(form.duration_min)) * 60, cur, 0) : "—"}<span className="text-muted-foreground font-normal text-xs"> · {form.duration_min || 0} min</span></span></div>
                </>); })()}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-lg" data-testid="service-save">Save service</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
